import * as React from 'react';
import { ErrorBoundaryLabel, onListingsFareApplied } from 'utility';
import { ErrorBoundary } from 'bernie-view';
import { SystemEventLevel } from 'bernie-logger';

import { SmartShoppingListingError, FlightsListings } from '@shared-ui/retail-flights-smart-shopping-listings';
import {
  areSameFiltersValuesList,
  hasSameNumberOfFilters,
  triggerSearchFromSideSheet,
  logEvent,
  getDetailsAndFaresContent
} from './utils';
import { useSmartShoppingListingsInput, useStores } from 'utility/hooks';

import { useHistory, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import {
  AddOnRequested,
  FlightsDetailsAndFaresContentFragmentFragment,
  FlightsDetailsAndFaresFooterFragmentFragment,
  FlightsSearchFilterValuesInput
} from 'src/__generated__/api';
import { removePill, selectClearAllFilters } from '../PWA_USF_FSR';
import { UitkSpacing, UitkSpacingProps } from 'uitk-react-spacing';

type SmartListings = {
  isSmallViewPort?: boolean;
};

const spacingListings: UitkSpacingProps['margin'] = { blockstart: 'six' };

export type DetailsAndFaresData = {
  content: FlightsDetailsAndFaresContentFragmentFragment;
  footer: FlightsDetailsAndFaresFooterFragmentFragment;
};
export const SmartListings = observer(function SmartListings(props: SmartListings) {
  const { isSmallViewPort } = props;
  const stores = useStores();
  const { listingsStore, searchCriteriaStore, errorStore, searchFormStore, detailsAndFaresStore } = stores;
  const smartShoppingInput = useSmartShoppingListingsInput();
  const { context, sortFilterStore } = useStores();
  const location = useLocation();
  const history = useHistory();
  const [detailsAndFaresData, setDetailsAndFaresData] = React.useState<DetailsAndFaresData>();
  const [isEBFSelected, setIsEBFSelected] = React.useState(false);
  const [selectedSanitizedOfferToken, setSelectedSanitizedOfferToken] = React.useState('');

  React.useEffect(() => {
    if (selectedSanitizedOfferToken) {
      getDetailsAndFaresContent(
        listingsStore.listingResult.listings,
        selectedSanitizedOfferToken,
        setDetailsAndFaresData,
        isEBFSelected
      );
    }
  }, [isEBFSelected, selectedSanitizedOfferToken, listingsStore.listingResult.listings]);

  const onLoad = React.useCallback(() => {
    logEvent(SystemEventLevel.INFO, 'SMART_SHOPPING_LOADED_LISTINGS', 'displaying new smart shoppping offers');
    listingsStore.setSmartListingsLoaded();
    sortFilterStore.setShowFiltersUSF(true);
  }, [listingsStore, sortFilterStore]);

  const onError = React.useCallback(
    (listingsErrorType: SmartShoppingListingError) => {
      listingsStore.setSmartListingsError(listingsErrorType);
      if (listingsErrorType === SmartShoppingListingError.NO_API_RESPONSE) {
        errorStore.noApiResponse = true;
      }
    },
    [listingsStore, errorStore]
  );

  const onTriggerClick = React.useCallback(
    response => onListingsFareApplied(response, stores, history, location, isSmallViewPort, true),
    [history, isSmallViewPort, location, stores]
  );

  const onDialogClose = React.useCallback(
    (flightSearchFilterValuesList: FlightsSearchFilterValuesInput[], addOnRequested: AddOnRequested[]) => {
      const { searchFilterValuesList } = searchCriteriaStore.searchCriteria;
      const sameAddOns =
        hasSameNumberOfFilters(searchFilterValuesList, flightSearchFilterValuesList) &&
        areSameFiltersValuesList(searchFilterValuesList, flightSearchFilterValuesList);

      if (!sameAddOns) {
        searchCriteriaStore.searchCriteria.searchFilterValuesList = JSON.parse(
          JSON.stringify(flightSearchFilterValuesList)
        );
        searchCriteriaStore.sideSheetQueryContext.addOnRequested = JSON.parse(JSON.stringify(addOnRequested));
        triggerSearchFromSideSheet(searchCriteriaStore, context, history, location);
      }

      return;
    },
    [searchCriteriaStore, context, history, location]
  );

  const onRemoveAllFilters = React.useCallback((): void => {
    selectClearAllFilters(searchCriteriaStore, context, history, location);
  }, [context, history, location, searchCriteriaStore]);

  const onRemoveFilter = React.useCallback(
    filter => {
      removePill(filter, searchCriteriaStore, context, history, location);
    },
    [context, history, location, searchCriteriaStore]
  );
  const isLoading = React.useCallback(
    (loadingState: boolean) => {
      listingsStore.setSmartListingsLoading(loadingState);
    },
    [listingsStore]
  );

  const onSuccessSideSheet = React.useCallback(() => {
    logEvent(SystemEventLevel.INFO, 'SMART_SHOPPING_SUCCESS_FSR_SIDE_SHEET', 'displaying data in FSR side sheet');
  }, []);

  const onErrorSideSheet = React.useCallback(() => {
    logEvent(SystemEventLevel.ERROR, 'SMART_SHOPPING_ERROR_FSR_SIDE_SHEET', 'displaying error in FSR side sheet');
  }, []);

  const onServerErrorClick = React.useCallback(
    error => {
      if (error.__typename === 'FlightsSearchCriteriaChangeAction') {
        if (isSmallViewPort) {
          return searchFormStore.setExpandSearchFormState(true);
        }

        searchFormStore.setShowDatePicker(true);
      }
    },
    [searchFormStore, isSmallViewPort]
  );

  /* Callback that takes a sanitized offer token upon card click. Made for AB 48260 */
  const onOfferSelect = React.useCallback((sanitizedOfferToken: string, isBargainFareOffer: boolean) => {
    if (sanitizedOfferToken) {
      setTimeout(() => {
        setSelectedSanitizedOfferToken(sanitizedOfferToken);
        setIsEBFSelected(isBargainFareOffer);
      });
    } else {
      logEvent(
        SystemEventLevel.ERROR,
        'WEB_FLIGHT_CARDS_NO_OFFERTOKEN',
        'No sanitized offer token was returned from smart listings'
      );
    }
  }, []);

  if (!searchCriteriaStore.searchCriteria.journeyCriteria[0].departureDate.day) {
    return null;
  }

  return (
    <ErrorBoundary label={ErrorBoundaryLabel.SMART_SHOPPING_LISTINGS}>
      <UitkSpacing margin={spacingListings}>
        <div>
          <FlightsListings
            inputs={smartShoppingInput}
            onDialogClose={onDialogClose}
            onError={onError}
            onErrorSideSheet={onErrorSideSheet}
            onFareApplied={onTriggerClick}
            onLoad={onLoad}
            onLoading={isLoading}
            onOfferSelect={onOfferSelect}
            onOverFilteringRemoveAllClick={onRemoveAllFilters}
            onOverFilteringRemovePillClick={onRemoveFilter}
            onSuccessSideSheet={onSuccessSideSheet}
            onServerErrorClick={onServerErrorClick}
            detailsAndFaresContent={detailsAndFaresData?.content}
            detailsAndFaresFooter={detailsAndFaresData?.footer}
            isUpsellLoading={detailsAndFaresStore.isUpsellLoading}
            shouldKeepLoading={listingsStore.loading}
          />
        </div>
      </UitkSpacing>
    </ErrorBoundary>
  );
});
